import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Globe,
  ExternalLink,
  Pencil,
  CheckCircle,
  XCircle,
  Loader2,
  Wifi,
  Trash2,
  Power,
  PowerOff,
} from 'lucide-react'

import { sitesApi } from '@/services/api'
import type { Site, SiteCreate, SiteUpdate, SiteCredentialsUpdate, PaginatedResponse, ConnectionTestResponse } from '@/types'
import { SiteModal } from '@/components/modals/SiteModal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

interface NewSiteForm {
  name: string
  url: string
  description: string
  wp_username: string
  wp_app_password: string
}

export function SitesPage() {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)
  const [testingId, setTestingId] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<{ siteId: string; result: ConnectionTestResponse } | null>(null)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editingSite, setEditingSite] = useState<Site | null>(null)
  const [newSite, setNewSite] = useState<NewSiteForm>({
    name: '',
    url: '',
    description: '',
    wp_username: '',
    wp_app_password: '',
  })
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery<PaginatedResponse<Site>>({
    queryKey: ['sites'],
    queryFn: () => sitesApi.list(),
  })

  const createSite = useMutation({
    mutationFn: (siteData: SiteCreate) => sitesApi.create(siteData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] })
      setShowAddForm(false)
      setNewSite({ name: '', url: '', description: '', wp_username: '', wp_app_password: '' })
    },
  })

  const deleteSite = useMutation({
    mutationFn: (siteId: string) => sitesApi.delete(siteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] })
    },
  })

  const updateSite = useMutation({
    mutationFn: ({ siteId, data }: { siteId: string; data: SiteUpdate }) => sitesApi.update(siteId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] })
      setEditModalOpen(false)
      setEditingSite(null)
    },
  })

  const updateCredentials = useMutation({
    mutationFn: ({ siteId, data }: { siteId: string; data: SiteCredentialsUpdate }) => sitesApi.updateCredentials(siteId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] })
      setEditModalOpen(false)
      setEditingSite(null)
    },
  })

  const activateSite = useMutation({
    mutationFn: (siteId: string) => sitesApi.activate(siteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] })
    },
  })

  const deactivateSite = useMutation({
    mutationFn: (siteId: string) => sitesApi.deactivate(siteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] })
    },
  })

  const testConnection = useMutation({
    mutationFn: async (siteId: string) => {
      setTestingId(siteId)
      const result = await sitesApi.testConnection(siteId)
      return { siteId, result }
    },
    onSuccess: ({ siteId, result }) => {
      setTestResult({ siteId, result })
      setTestingId(null)
    },
    onError: () => {
      setTestingId(null)
    },
  })

  const filteredSites = data?.items.filter(
    (site) =>
      site.name.toLowerCase().includes(search.toLowerCase()) ||
      site.url.toLowerCase().includes(search.toLowerCase())
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createSite.mutate(newSite)
  }

  const handleDelete = (siteId: string, siteName: string) => {
    if (window.confirm(t('sites.confirmDelete', { name: siteName }))) {
      deleteSite.mutate(siteId)
    }
  }

  const handleEdit = (site: Site) => {
    setEditingSite(site)
    setEditModalOpen(true)
  }

  const handleUpdateSite = async (data: SiteCreate | SiteUpdate) => {
    if (editingSite) {
      await updateSite.mutateAsync({ siteId: editingSite.id, data: data as SiteUpdate })
    }
  }

  const handleUpdateCredentials = async (data: SiteCredentialsUpdate) => {
    if (editingSite) {
      await updateCredentials.mutateAsync({ siteId: editingSite.id, data })
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('sites.title')}</h1>
          <p className="text-muted-foreground">{t('sites.description')}</p>
        </div>
        <Button onClick={() => setShowAddForm(true)}>
          <Plus className="me-2 h-4 w-4" />
          {t('sites.addSite')}
        </Button>
      </div>

      {/* Add Site Form */}
      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>{t('sites.addWordPressSite')}</CardTitle>
            <CardDescription>
              {t('sites.addSiteDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('sites.siteName')}</Label>
                  <Input
                    id="name"
                    placeholder={t('sites.siteNamePlaceholder')}
                    value={newSite.name}
                    onChange={(e) => setNewSite({ ...newSite, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="url">{t('sites.wordPressUrl')}</Label>
                  <Input
                    id="url"
                    type="url"
                    placeholder={t('sites.urlPlaceholder')}
                    value={newSite.url}
                    onChange={(e) => setNewSite({ ...newSite, url: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">{t('sites.descriptionLabel')}</Label>
                <Input
                  id="description"
                  placeholder={t('sites.descriptionPlaceholder')}
                  value={newSite.description}
                  onChange={(e) => setNewSite({ ...newSite, description: e.target.value })}
                />
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3">{t('sites.authentication')}</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('sites.authDescription')}
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="wp_username">{t('sites.wpUsername')}</Label>
                    <Input
                      id="wp_username"
                      placeholder={t('sites.wpUsernamePlaceholder')}
                      value={newSite.wp_username}
                      onChange={(e) => setNewSite({ ...newSite, wp_username: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="wp_app_password">{t('sites.appPassword')}</Label>
                    <Input
                      id="wp_app_password"
                      type="password"
                      placeholder={t('sites.appPasswordPlaceholder')}
                      value={newSite.wp_app_password}
                      onChange={(e) => setNewSite({ ...newSite, wp_app_password: e.target.value })}
                      required
                    />
                  </div>
                </div>
              </div>

              {createSite.error && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                  {(createSite.error as Error).message}
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button type="submit" disabled={createSite.isPending}>
                  {createSite.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                  {t('sites.addSite')}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                  {t('app.cancel')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      {!showAddForm && (
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('sites.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ps-9"
            />
          </div>
        </div>
      )}

      {isLoading && <p className="text-muted-foreground">{t('sites.loadingSites')}</p>}

      {error && <p className="text-destructive">{t('sites.errorLoadingSites')} {(error as Error).message}</p>}

      {!showAddForm && filteredSites && filteredSites.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Globe className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">{t('sites.noSitesYet')}</p>
            <p className="text-muted-foreground text-center mt-1">
              {t('sites.addSitesDescription')}
            </p>
            <Button className="mt-4" onClick={() => setShowAddForm(true)}>
              <Plus className="me-2 h-4 w-4" />
              {t('sites.addYourFirstSite')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Sites Grid */}
      {!showAddForm && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredSites?.map((site) => (
            <Card key={site.id} className={!site.is_active ? 'opacity-60' : ''}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      {site.name}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-1 mt-1">
                      <a
                        href={site.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline flex items-center gap-1"
                      >
                        {new URL(site.url).hostname}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </CardDescription>
                  </div>
                  <div
                    className={`p-1.5 rounded-full ${site.has_credentials && site.is_active ? 'bg-green-500' : 'bg-gray-400'}`}
                  >
                    {site.has_credentials && site.is_active ? (
                      <CheckCircle className="h-3 w-3 text-white" />
                    ) : (
                      <XCircle className="h-3 w-3 text-white" />
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">{site.description || t('sites.noDescription')}</p>

                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm">
                    {site.has_credentials ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" /> {t('sites.credentialsSet')}
                      </span>
                    ) : (
                      <span className="text-muted-foreground flex items-center gap-1">
                        <XCircle className="h-3 w-3" /> {t('sites.noCredentials')}
                      </span>
                    )}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => testConnection.mutate(site.id)}
                    disabled={testingId === site.id || !site.has_credentials}
                  >
                    {testingId === site.id ? (
                      <Loader2 className="me-1 h-3 w-3 animate-spin" />
                    ) : (
                      <Wifi className="me-1 h-3 w-3" />
                    )}
                    {t('sites.test')}
                  </Button>
                </div>

                {/* Test Result */}
                {testResult?.siteId === site.id && (
                  <div
                    className={`mb-4 p-2 rounded text-xs ${testResult.result.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}
                  >
                    {testResult.result.success ? (
                      <>
                        <strong>{t('sites.connected')}</strong> {testResult.result.site_name} (WP {testResult.result.wp_version})
                      </>
                    ) : (
                      <>
                        <strong>{t('sites.connectionFailed')}</strong> {testResult.result.message}
                      </>
                    )}
                  </div>
                )}

                {/* Status & Last Published */}
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                  <span>{site.is_active ? t('sites.activeStatus') : t('sites.inactiveStatus')}</span>
                  <span>
                    {site.last_published_at
                      ? `${t('sites.lastPublished')} ${new Date(site.last_published_at).toLocaleDateString()}`
                      : t('sites.neverPublished')}
                  </span>
                </div>

                <div className="flex items-center justify-between pt-3 border-t gap-2">
                  <div className="flex gap-1">
                    {site.is_active ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deactivateSite.mutate(site.id)}
                        disabled={deactivateSite.isPending}
                        title={t('sites.deactivateSite')}
                      >
                        <PowerOff className="h-3 w-3" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => activateSite.mutate(site.id)}
                        disabled={activateSite.isPending}
                        title={t('sites.activateSite')}
                      >
                        <Power className="h-3 w-3" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(site.id, site.name)}
                      disabled={deleteSite.isPending}
                      title={t('sites.deleteSite')}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(site)}>
                    <Pencil className="me-1 h-3 w-3" />
                    {t('app.edit')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {data && data.pages > 1 && (
        <div className="flex justify-center gap-2">
          <p className="text-sm text-muted-foreground">
            Page {data.page} of {data.pages} ({data.total} total)
          </p>
        </div>
      )}

      {/* Edit Site Modal */}
      <SiteModal
        open={editModalOpen}
        onOpenChange={(open) => {
          setEditModalOpen(open)
          if (!open) setEditingSite(null)
        }}
        site={editingSite}
        onSubmit={handleUpdateSite}
        onUpdateCredentials={handleUpdateCredentials}
        isSubmitting={updateSite.isPending || updateCredentials.isPending}
      />
    </div>
  )
}
